import { useEffect, useRef, useState } from 'react';
import { getPresignedUrl } from '../../../../lib/presigned-url-cache';
import { displayNameOf, type DatedAttachment } from '../../../../lib/attachment-groups';
import { Paperclip } from '../../../../icons';
import { tChat } from '../../../../lib/chat-i18n';

/**
 * 미디어 브라우저 이미지 타일 (chat-media-date-groups — 2026-08-07).
 *
 * 기존 `MediaImageThumb` 와 두 가지가 다르다:
 *   1) **클릭 = 새 탭이 아니라 라이트박스** — 파일이 '미리보기 패널' 을 갖는 것과 대칭을 맞춘다
 *      ("이미지도 파일과 동일한 기능").
 *   2) **presign 을 캐시 + 뷰포트 게이트로 감싼다** — 기존 구현은 썸네일 1개당 POST 1회를 무조건
 *      날렸다. 일자 그룹을 펼치면 동시 렌더 수가 늘 수 있어 필요한 안전장치다.
 *
 * ⚠ jsdom 에는 `IntersectionObserver` 가 없고 `src/test/setup.ts` 에 폴리필도 없다 →
 * 미지원 환경에서는 즉시 로드로 폴백한다(테스트 setup 을 건드리지 않기 위한 컴포넌트측 가드).
 *
 * CSS 는 기존 `.chat-media-thumb` 계열을 그대로 쓴다.
 */
export function MediaImageTile({
  attachment,
  onOpen,
}: {
  attachment: DatedAttachment;
  onOpen: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLButtonElement | null>(null);

  // 뷰포트 근처에 올 때만 presign 요청. 미지원 환경(jsdom 등)은 즉시 로드.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    getPresignedUrl(attachment.fileId)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, attachment.fileId]);

  const name = displayNameOf(attachment);

  return (
    <button
      ref={ref}
      type="button"
      className={`chat-media-thumb${url ? '' : ' chat-media-thumb--placeholder'}`}
      onClick={onOpen}
      aria-label={tChat('chat.media.image_open_aria', { name })}
      title={name}
      aria-busy={!url && !error}
    >
      {url ? <img src={url} alt={name} loading="lazy" /> : <Paperclip size={14} aria-hidden />}
    </button>
  );
}
